import React, { useEffect, useState } from "react";
import { Save, Eye, EyeOff, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

export default function AdminEmail() {
  const [cfg, setCfg] = useState(null);
  const [reveal, setReveal] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/admin/email-config");
      setCfg({ ...data, api_key_changed: false });
    })();
  }, []);

  if (!cfg) return null;

  const save = async () => {
    try {
      const payload = {
        enabled: cfg.enabled,
        provider: cfg.provider || "resend",
        api_key: cfg.api_key_changed ? cfg.api_key : "••••••••",
        sender_email: cfg.sender_email,
        sender_name: cfg.sender_name,
      };
      await api.put("/admin/email-config", payload);
      toast.success("Email configuration saved");
      setCfg({ ...cfg, api_key_changed: false, api_key: "••••••••" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const sendTest = async () => {
    if (!testEmail) { toast.error("Enter a recipient email"); return; }
    setTesting(true);
    try {
      await api.post("/admin/email-config/test", { recipient: testEmail });
      toast.success("Test email sent — check the inbox");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="p-8 max-w-3xl">
      <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">// email / otp delivery</div>
      <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Email Settings</h1>
      <p className="text-sm text-zinc-400 mt-2">Configure the email provider used for one-time codes and 2FA reset confirmations.</p>

      <Card className="bg-zinc-900/60 border-zinc-800 p-6 mt-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium">Enable email delivery</Label>
            <div className="text-xs text-zinc-500 mt-1">When disabled, email-OTP and 2FA reset will not be available to users.</div>
          </div>
          <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="email-enabled-toggle" />
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">Provider</Label>
          <div className="mt-1 flex items-center gap-2 text-sm">
            <span className="px-2.5 py-1 rounded bg-zinc-800 font-mono text-xs">Resend</span>
            <span className="text-xs text-zinc-500">Resend.com — modern transactional email API</span>
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">Resend API key</Label>
          <div className="relative mt-1">
            <Input
              type={reveal ? "text" : "password"}
              value={cfg.api_key}
              onChange={(e) => setCfg({ ...cfg, api_key: e.target.value, api_key_changed: true })}
              onFocus={() => { if (!cfg.api_key_changed) setCfg({ ...cfg, api_key: "", api_key_changed: true }); }}
              className="bg-zinc-950 border-zinc-800 font-mono text-xs pr-10"
              placeholder="re_xxxxxxxxxx"
              data-testid="email-apikey-input"
            />
            <button type="button" onClick={() => setReveal(!reveal)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-zinc-800">
              {reveal ? <EyeOff className="w-4 h-4 text-zinc-400" /> : <Eye className="w-4 h-4 text-zinc-400" />}
            </button>
          </div>
          <div className="text-xs text-zinc-600 mt-1">Get one at <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-zinc-400 underline">resend.com/api-keys</a>. Stored encrypted-at-rest.</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Sender email</Label>
            <Input value={cfg.sender_email} onChange={(e) => setCfg({ ...cfg, sender_email: e.target.value })} className="bg-zinc-950 border-zinc-800 mt-1 font-mono text-xs" placeholder="noreply@yourdomain.com" data-testid="email-sender-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Sender display name</Label>
            <Input value={cfg.sender_name} onChange={(e) => setCfg({ ...cfg, sender_name: e.target.value })} className="bg-zinc-950 border-zinc-800 mt-1" placeholder="NexusID" data-testid="email-sender-name-input" />
          </div>
        </div>

        <div className="pt-2">
          <Button onClick={save} className="bg-white text-zinc-950 hover:bg-zinc-200" data-testid="email-save-btn">
            <Save className="w-4 h-4 mr-1.5" /> Save configuration
          </Button>
        </div>
      </Card>

      <Card className="bg-zinc-900/60 border-zinc-800 p-6 mt-4 space-y-3">
        <h3 className="font-display text-sm font-medium">Send a test email</h3>
        <div className="flex items-center gap-2">
          <Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="recipient@email.com" className="bg-zinc-950 border-zinc-800 font-mono text-xs" data-testid="email-test-recipient" />
          <Button onClick={sendTest} disabled={testing || !cfg.enabled} className="bg-white text-zinc-950 hover:bg-zinc-200" data-testid="email-test-send-btn">
            <Send className="w-4 h-4 mr-1.5" /> Send test
          </Button>
        </div>
        <div className="text-xs text-zinc-600">A sample OTP-style email will be delivered using the current configuration.</div>
      </Card>

      <Card className="bg-zinc-900/40 border-zinc-800 p-5 mt-4">
        <h3 className="font-display text-sm font-medium mb-2 flex items-center gap-2"><AlertCircle className="w-4 h-4 text-amber-400" /> Setup tips</h3>
        <ol className="space-y-1.5 text-xs text-zinc-400 list-decimal list-inside font-mono">
          <li>Sign up at resend.com and create an API key</li>
          <li>Add and verify your sending domain (DNS records) — until verified, Resend only delivers to your own verified address</li>
          <li>Use a sender like noreply@yourdomain.com</li>
          <li>Save the configuration here, then click &quot;Send test&quot;</li>
        </ol>
      </Card>
    </div>
  );
}
